import React from "react";
import { CitationButton } from "@/components/feature/layout/chat/citationButton";
import { type Evidence } from "@/types/evidences";

const processBoldText = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\*\*.+?\*\*)/);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const processLine = (line: string, evidences: Evidence[], globalCitationIndex: number): [React.ReactNode[], number] => {
  const citationRegex = /\[(?:Id:)?(\d+(?:,\s*(?:Id:)?\d+)*)\](?!\()/g;
  let lastIndex = 0;
  const processedLine: React.ReactNode[] = [];

  let match;

  while ((match = citationRegex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      processedLine.push(...processBoldText(line.slice(lastIndex, match.index)));
    }

    const citationIds = match[1]
      .split(/,\s*/)
      .map(Number)
      .filter(id => !isNaN(id));
    const citationButtons = citationIds
      .map(citationId => {
        const evidence = evidences.find(ev => parseInt(ev.citationId, 10) === citationId);
        if (evidence) {
          const button = (
            <CitationButton
              key={`citation-${evidence.id}-${evidence.documentId}-${citationId}-${globalCitationIndex}`}
              citation={citationId}
              highlights={evidence.highlights}
              fseId={evidence.fseId}
              documentId={evidence.documentId}
              index={globalCitationIndex}
            />
          );
          globalCitationIndex++;
          return button;
        }
        return null;
      })
      .filter(Boolean);

    if (citationButtons.length) {
      processedLine.push(
        <span key={`citation-group-${globalCitationIndex}`} className="inline-flex items-center">
          {citationButtons}
        </span>,
      );
    }
    lastIndex = match.index + match[0].length;
  }

  const uuidRegex = /\[([\w\-\s,]+)\]/g;

  while ((match = uuidRegex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      processedLine.push(...processBoldText(line.slice(lastIndex, match.index)));
    }

    const citationButtons = match[1]
      .split(",")
      .map(id => id.trim())
      .map(citationId => {
        const evidence = evidences.find(ev => ev.id.replace("evidence-", "") === citationId);
        if (evidence) {
          const button = (
            <CitationButton
              key={`citation-${evidence.id}-${evidence.documentId}-${citationId}-${globalCitationIndex}`}
              citation={citationId}
              highlights={evidence.highlights}
              fseId={evidence.fseId}
              documentId={evidence.documentId}
              index={globalCitationIndex}
            />
          );
          globalCitationIndex++;
          return button;
        }
        return null;
      })
      .filter(Boolean);

    if (citationButtons.length) {
      processedLine.push(
        <span key={`citation-group-${globalCitationIndex}`} className="inline-flex items-center">
          {citationButtons}
        </span>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < line.length && !!line.length) {
    processedLine.push(...processBoldText(line.slice(lastIndex)));
  }

  return [processedLine, globalCitationIndex];
};

export const processCitationsInText = (text: string, evidences: Evidence[]): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const lines = text.split("\n");
  let globalCitationIndex = 0;
  let isIndented = false;
  let inNumberedList = false;

  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim();
    if (trimmedLine === "") {
      // Skip empty lines without adding <br/> tags
      return;
    }

    const [processedLine, newIndex] = processLine(line, evidences, globalCitationIndex);
    globalCitationIndex = newIndex;

    if (lineIndex === 0 && /^\*\*.+\*\*$/.test(trimmedLine)) {
      // First line is bolded, make it an h2 heading
      const headingText = trimmedLine.replace(/^\*\*(.+)\*\*$/, "$1");
      parts.push(
        <h2 key={`h2-${lineIndex}`} className="mt-6 text-2xl font-normal">
          {headingText}
        </h2>,
      );
      isIndented = false;
      inNumberedList = false;
    } else if (/^\d+\.\s\*\*.+\*\*$/.test(trimmedLine)) {
      // Numbered heading
      const [number, ...rest] = trimmedLine.split(/\s(.+)/);
      parts.push(
        <h3 key={`h3-${lineIndex}`} className="mt-4 font-normal">
          {number} {processBoldText(rest.join(" "))}
        </h3>,
      );
      isIndented = true;
      inNumberedList = true;
    } else if (/^\d+\.\s.+/.test(trimmedLine)) {
      // Numbered list item
      parts.push(
        <p key={`p-${lineIndex}`} className="ml-4">
          {processedLine}
        </p>,
      );
      isIndented = true;
      inNumberedList = true;
    } else {
      // Regular paragraph
      const className = isIndented && inNumberedList ? "ml-4" : "ml-0";
      parts.push(
        <p key={`p-${lineIndex}`} className={className}>
          {processedLine}
        </p>,
      );
    }

    // Reset indentation if a new heading is encountered
    if (!/^\d+\.\s.+/.test(trimmedLine) && !/^\d+\.\s\*\*.+\*\*$/.test(trimmedLine)) {
      isIndented = false;
      inNumberedList = false;
    }
  });

  return parts;
};

export const preprocessMarkdown = (text: string): string => {
  // Preserve numbered headings
  const preservedHeadings = text.replace(
    /^(\d+\.)\s+(\*\*.+\*\*)/gm,
    (_, number, heading) => `__PRESERVED_HEADING__${number} ${heading}`,
  );

  // Process list items (excluding preserved headings)
  const processedListItems = preservedHeadings.replace(
    /^(\d+\.)\s+(?!__PRESERVED_HEADING__)([\s\S]*?)(?=(?:\n\d+\.\s|\n\n|$))/gm,
    (match, number, content) => {
      if (typeof content === "string") {
        return `${number} ${content.replace(/\n\s+/g, " ").trim()}\n\n`;
      }
      return match;
    },
  );

  // Restore preserved headings
  return processedListItems.replace(/__PRESERVED_HEADING__/g, "");
};
